import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

import { useUsers } from '../../context/users';
import { Unit, User } from '../../context/model/types';
import ItemModal from '../Modals/ItemModal';
import UnitModal from '../Modals/UnitModal';
import UserModal from '../Modals/UserModal/UserModal';

// ── helpers ────────────────────────────────────────────────────────────────

const isOccupied = (u: Unit) => u.availability === 'RENTED';

const parseUsage = (usage: string): number => {
  if (!usage) return 0;
  const n = parseFloat(usage);
  if (isNaN(n)) return 0;
  return n > 1 ? Math.min(n, 100) : Math.round(n * 100);
};

const formatSize = (size: string) =>
  size ? size.replace(/x/i, '×') : '—';

// ── sub-components ─────────────────────────────────────────────────────────

const StatusBadge = ({ occupied }: { occupied: boolean }) => (
  <span
    aria-label={occupied ? 'Occupied' : 'Vacant'}
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.2,
      backgroundColor: occupied ? '#E1F5EE' : '#F0F0F0',
      color: occupied ? '#085041' : '#888',
    }}
  >
    {occupied ? 'Occupied' : 'Vacant'}
  </span>
);

const UsageBar = ({ usage, occupied }: { usage: string; occupied: boolean }) => {
  const pct = parseUsage(usage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 80 }}>
      <div
        style={{
          flex: 1,
          height: 4,
          borderRadius: 2,
          backgroundColor: '#E5E7EB',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 2,
            backgroundColor: occupied ? '#1D9E75' : '#C4C4C4',
            transition: 'width 0.2s',
          }}
        />
      </div>
      <span style={{ fontSize: 12, color: '#888', width: 34, textAlign: 'right', flexShrink: 0 }}>
        {pct}%
      </span>
    </div>
  );
};

// ── sort helpers ────────────────────────────────────────────────────────────

type SortKey = 'unitID' | 'size' | 'usage' | 'price' | 'availability';

const sortUnits = (units: Unit[], key: SortKey, dir: 'asc' | 'desc'): Unit[] => {
  const mult = dir === 'asc' ? 1 : -1;
  return [...units].sort((a, b) => {
    if (key === 'availability') {
      const aO = isOccupied(a) ? 0 : 1;
      const bO = isOccupied(b) ? 0 : 1;
      return mult * (aO - bO || (a.unitID || '').localeCompare(b.unitID || ''));
    }
    if (key === 'usage') return mult * (parseUsage(a.usage) - parseUsage(b.usage));
    if (key === 'price') {
      return mult * (parseFloat(a.price || '0') - parseFloat(b.price || '0'));
    }
    const av = (a[key] as string) || '';
    const bv = (b[key] as string) || '';
    return mult * av.localeCompare(bv);
  });
};

// ── main component ─────────────────────────────────────────────────────────

const InventoryTable = (): JSX.Element => {
  const { units, users } = useUsers();

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(units);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'DataSheet.xlsx');
  };

  const [showItemModal, setShowItemModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [unitID, setUnitID] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Occupied' | 'Vacant'>('All');
  const [sortKey, setSortKey] = useState<SortKey>('availability');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const total = units.length;
  const occupiedCount = units.filter(isOccupied).length;
  const vacantCount = total - occupiedCount;

  const filtered = useMemo(() => {
    let result = [...units];
    if (statusFilter === 'Occupied') result = result.filter(isOccupied);
    if (statusFilter === 'Vacant') result = result.filter(u => !isOccupied(u));
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        u =>
          (u.unitID || '').toLowerCase().includes(q) ||
          (users.find(usr => usr.unitID === u.id.toString())?.name || '').toLowerCase().includes(q),
      );
    }
    return sortUnits(result, sortKey, sortDir);
  }, [units, search, statusFilter, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortCaret = ({ col }: { col: SortKey }) => {
    if (col !== sortKey) return <span style={{ color: '#CCC', marginLeft: 4 }}>↕</span>;
    return (
      <span style={{ marginLeft: 4, color: '#7B1FA2' }}>
        {sortDir === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const thStyle = (sortable = false, align: 'left' | 'right' = 'left'): React.CSSProperties => ({
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 500,
    color: '#6B7280',
    letterSpacing: 0.3,
    textAlign: align,
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    userSelect: 'none',
    cursor: sortable ? 'pointer' : 'default',
    whiteSpace: 'nowrap',
  });

  const tdStyle = (align: 'left' | 'right' = 'left', muted = false): React.CSSProperties => ({
    padding: '10px 12px',
    fontSize: 13,
    color: muted ? '#9CA3AF' : '#111827',
    textAlign: align,
    verticalAlign: 'middle',
    borderBottom: '1px solid #F3F4F6',
  });

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* ── toolbar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
          {total} units · {occupiedCount} occupied · {vacantCount} vacant
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search unit or tenant..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: '7px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              color: '#111827',
              outline: 'none',
              width: 210,
            }}
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
            style={{
              padding: '7px 10px',
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              fontSize: 13,
              color: '#374151',
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
          >
            <option>All</option>
            <option>Occupied</option>
            <option>Vacant</option>
          </select>
          <button
            type="button"
            onClick={exportToExcel}
            style={{
              padding: '7px 14px',
              borderRadius: 6,
              border: '1px solid #7B1FA2',
              backgroundColor: '#fff',
              color: '#7B1FA2',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Export
          </button>
          <button
            type="button"
            onClick={() => { setUnitID(0); setShowUnitModal(true); }}
            style={{
              padding: '7px 14px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: '#7B1FA2',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + Add unit
          </button>
        </div>
      </div>

      {/* ── table card ── */}
      <div
        style={{
          backgroundColor: '#fff',
          border: '0.5px solid #E5E7EB',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={thStyle(true)} onClick={() => handleSort('unitID')}>
                  Unit <SortCaret col="unitID" />
                </th>
                <th style={thStyle(true)} onClick={() => handleSort('availability')}>
                  Status <SortCaret col="availability" />
                </th>
                <th style={thStyle()}>Tenant</th>
                <th style={thStyle(true)} onClick={() => handleSort('size')}>
                  Size (ft²) <SortCaret col="size" />
                </th>
                <th style={thStyle(true)} onClick={() => handleSort('usage')}>
                  Usage <SortCaret col="usage" />
                </th>
                <th style={{ ...thStyle(true, 'right') }} onClick={() => handleSort('price')}>
                  Price <SortCaret col="price" />
                </th>
                <th style={thStyle(false, 'right')}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                    No units match your filters.
                  </td>
                </tr>
              )}
              {filtered.map(unit => {
                const occupied = isOccupied(unit);
                const rowBg = occupied ? '#fff' : '#FAFAFA';
                return (
                  <tr
                    key={unit.id}
                    onClick={() => { setUnitID(unit.id); setShowItemModal(true); }}
                    style={{
                      backgroundColor: rowBg,
                      cursor: 'pointer',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5F3FF')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = rowBg)}
                  >
                    {/* Unit ID */}
                    <td style={tdStyle('left', !occupied)}>
                      <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                        {unit.unitID || '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={tdStyle()}>
                      <StatusBadge occupied={occupied} />
                    </td>

                    {/* Tenant */}
                    <td
                      style={tdStyle('left', !occupied)}
                      onClick={e => {
                        e.stopPropagation();
                        const user = users.find(u => u.unitID === unit.id.toString());
                        if (user) { setSelectedUser(user); setShowUserModal(true); }
                      }}
                    >
                      {(() => {
                        const user = users.find(u => u.unitID === unit.id.toString());
                        if (!user) return <span style={{ color: '#9CA3AF' }}>—</span>;
                        return (
                          <span style={{ color: '#7B1FA2', textDecoration: 'underline', cursor: 'pointer' }}>
                            {user.name || '—'}
                          </span>
                        );
                      })()}
                    </td>

                    {/* Size */}
                    <td style={tdStyle('left', !occupied)}>
                      {formatSize(unit.size)}
                    </td>

                    {/* Usage */}
                    <td style={{ ...tdStyle(), minWidth: 120 }}>
                      <UsageBar usage={unit.usage} occupied={occupied} />
                    </td>

                    {/* Price */}
                    <td style={{ ...tdStyle('right', !occupied), fontVariantNumeric: 'tabular-nums' }}>
                      {unit.price || '—'}
                    </td>

                    {/* Actions */}
                    <td
                      style={{ ...tdStyle('right'), whiteSpace: 'nowrap' }}
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => { setUnitID(unit.id); setShowItemModal(true); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '2px 4px',
                          fontSize: 13,
                          color: '#7B1FA2',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        View
                      </button>
                      <span style={{ color: '#D1D5DB', margin: '0 4px' }}>·</span>
                      <button
                        type="button"
                        onClick={() => { setUnitID(unit.id); setShowUnitModal(true); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '2px 4px',
                          fontSize: 13,
                          color: '#6B7280',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          fontFamily: 'inherit',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showItemModal && <ItemModal setShow={setShowItemModal} unitID={unitID} />}
      {showUnitModal && <UnitModal setShow={setShowUnitModal} unitID={unitID} />}
      {showUserModal && selectedUser && <UserModal setShow={setShowUserModal} user={selectedUser} type="customer" />}
    </div>
  );
};

export default InventoryTable;
